import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionButtons,
  Badge,
  DataTable,
  DataTableToggle,
  DATA_TABLE_SHOWCASE_VARIANTS,
  DATA_TABLE_STATE_LABELS,
  DATA_TABLE_STATES,
  type DataTableColumn,
  type DataTableGroup,
  type DataTableRow,
  type DataTableShowcaseVariantId,
  type DataTableState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'

const DEFAULT_COLUMNS: DataTableColumn[] = [
  { id: 'col1', label: 'Header', sortable: true },
  { id: 'col2', label: 'Header', sortable: true },
  { id: 'col3', label: 'Header', sortable: true },
]

const EXPANDED_COLUMNS: DataTableColumn[] = [
  { id: 'col1', label: 'Column title 1', sortable: true, showInfo: true },
  { id: 'col2', label: 'Column title 2', sortable: true, showInfo: true },
  { id: 'col3', label: 'Column title 3', sortable: true, showInfo: true },
]

const createDefaultRows = (count: number): DataTableRow[] =>
  Array.from({ length: count }, (_, index) => ({
    id: String(index + 1),
    col1: 'Content',
    col2: 'Content',
    col3: 'Content',
  }))

type SwitchBadgeRow = DataTableRow & {
  active: boolean
  status: string
}

const SWITCH_BADGE_ROWS: SwitchBadgeRow[] = [
  { id: '1', col1: 'Content', col2: 'Content', status: 'Active', active: true },
  {
    id: '2',
    col1: 'Content',
    col2: 'Content',
    status: 'Inactive',
    active: false,
  },
  { id: '3', col1: 'Content', col2: 'Content', status: 'Active', active: true },
]

const GROUPED_SAMPLE: DataTableGroup[] = [
  {
    id: 'group-1',
    title: 'Group title',
    rows: createDefaultRows(3),
  },
  { id: 'group-2', title: 'Group title', rows: createDefaultRows(1) },
  { id: 'group-3', title: 'Group title', rows: createDefaultRows(1) },
]

const NESTED_HEADER_COLUMNS: DataTableColumn[] = [
  { id: 'detail1', label: 'Column title A' },
  { id: 'detail2', label: 'Column title B' },
  { id: 'detail3', label: 'Column title C' },
]

const NESTED_LEDGER_COLUMNS: DataTableColumn[] = [
  { id: 'appointment', label: 'Appointment' },
  { id: 'provider', label: 'Provider' },
  { id: 'amount', label: 'Amount' },
]

const NESTED_PROCEDURE_COLUMNS: DataTableColumn[] = [
  { id: 'code', label: 'Code' },
  { id: 'description', label: 'Description' },
  { id: 'fee', label: 'Fee' },
]

const getNestedHeaderRows = (parentRow: DataTableRow): DataTableRow[] => [
  {
    id: `${parentRow.id}-nested`,
    detail1: 'Content',
    detail2: 'Content',
    detail3: 'Content',
  },
]

const getNestedLedgerRows = (parentRow: DataTableRow): DataTableRow[] => [
  {
    id: `${parentRow.id}-ledger-1`,
    appointment: 'Cleaning',
    provider: 'Dr. Smith',
    amount: '$120',
  },
  {
    id: `${parentRow.id}-ledger-2`,
    appointment: 'Exam',
    provider: 'Dr. Lee',
    amount: '$85',
  },
]

const getNestedProcedureRows = (ledgerRow: DataTableRow): DataTableRow[] => [
  {
    id: `${ledgerRow.id}-proc-1`,
    code: 'D1110',
    description: 'Prophylaxis',
    fee: '$90',
  },
  {
    id: `${ledgerRow.id}-proc-2`,
    code: 'D0150',
    description: 'Comprehensive eval',
    fee: '$30',
  },
]

const STATIC_PAGINATION = {
  pageSize: 25,
  currentPage: 1,
  totalPages: 3,
  onPageChange: () => undefined,
  onPageSizeChange: () => undefined,
}

const DataTableShowcase: React.FC = () => {
  const [highlightedState, setHighlightedState] =
    useState<DataTableState>('default')
  const [highlightedVariant, setHighlightedVariant] =
    useState<DataTableShowcaseVariantId>('default')
  const [rowCount, setRowCount] = useState(3)
  const [selectable, setSelectable] = useState(false)
  const [showPagination, setShowPagination] = useState(true)
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])
  const [sortColumnId, setSortColumnId] = useState('col1')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(24)
  const [expandable, setExpandable] = useState(false)
  const [nestedTableExpand, setNestedTableExpand] = useState(false)
  const [nestedDoubleExpand, setNestedDoubleExpand] = useState(false)
  const [showSearch, setShowSearch] = useState(true)
  const [searchValue, setSearchValue] = useState('')
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>(['1'])
  const [darkMode, setDarkMode] = useState(false)
  const stateRefs = useRef<
    Partial<Record<DataTableState, HTMLDivElement | null>>
  >({})
  const variantRefs = useRef<
    Partial<Record<DataTableShowcaseVariantId, HTMLDivElement | null>>
  >({})
  const skipInitialStateScroll = useRef(true)
  const skipInitialVariantScroll = useRef(true)

  const liveRows = useMemo(() => createDefaultRows(rowCount), [rowCount])

  const switchBadgeColumns: DataTableColumn<SwitchBadgeRow>[] = useMemo(
    () => [
      {
        id: 'active',
        label: 'Column title 1',
        sortable: true,
        renderCell: (row) => (
          <DataTableToggle
            checked={row.active}
            ariaLabel={`Toggle row ${row.id}`}
          />
        ),
      },
      { id: 'col1', label: 'Column title 2', sortable: true },
      { id: 'col2', label: 'Column title 3', sortable: true },
      {
        id: 'status',
        label: 'Column title 4',
        sortable: true,
        renderCell: (row) => (
          <Badge
            mode="tinted"
            color={row.status === 'Active' ? 'blue' : 'gray'}
            label={row.status}
            darkMode={darkMode}
          />
        ),
      },
    ],
    [darkMode],
  )

  const infoColumns: DataTableColumn[] = useMemo(
    () => [
      { id: 'col1', label: 'Header', sortable: true, showInfo: true },
      { id: 'col2', label: 'Header', sortable: true },
      { id: 'col3', label: 'Header', sortable: true },
    ],
    [],
  )

  useEffect(() => {
    if (skipInitialStateScroll.current) {
      skipInitialStateScroll.current = false
      return
    }

    stateRefs.current[highlightedState]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedState])

  useEffect(() => {
    if (skipInitialVariantScroll.current) {
      skipInitialVariantScroll.current = false
      return
    }

    variantRefs.current[highlightedVariant]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedVariant])

  const handleSort = (columnId: string): void => {
    if (sortColumnId === columnId) {
      setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSortColumnId(columnId)
    setSortDirection('asc')
  }

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'data-table-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="data-table-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(event.target.value as DataTableState)
                }
              >
                {DATA_TABLE_STATES.map((option) => (
                  <option key={option} value={option}>
                    {DATA_TABLE_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'data-table-highlight-variant',
            label: 'Highlighted variant',
            control: (
              <select
                id="data-table-highlight-variant"
                className="ui-field-control"
                value={highlightedVariant}
                onChange={(event) =>
                  setHighlightedVariant(
                    event.target.value as DataTableShowcaseVariantId,
                  )
                }
              >
                {DATA_TABLE_SHOWCASE_VARIANTS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'data-table-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="data-table-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'data-table-row-count',
            label: 'Row count',
            control: (
              <input
                id="data-table-row-count"
                className="ui-field-control"
                type="number"
                min={1}
                max={6}
                value={rowCount}
                onChange={(event) =>
                  setRowCount(Number(event.target.value) || 1)
                }
              />
            ),
          },
          {
            id: 'data-table-selectable',
            label: 'Selectable rows',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="data-table-selectable"
                  type="checkbox"
                  checked={selectable}
                  onChange={(event) => setSelectable(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'data-table-pagination',
            label: 'Pagination',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="data-table-pagination"
                  type="checkbox"
                  checked={showPagination}
                  onChange={(event) => setShowPagination(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'data-table-search',
            label: 'Search',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="data-table-search"
                  type="checkbox"
                  checked={showSearch}
                  onChange={(event) => setShowSearch(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'data-table-expandable',
            label: 'Expandable rows',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="data-table-expandable"
                  type="checkbox"
                  checked={expandable}
                  onChange={(event) => setExpandable(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'data-table-nested-table-expand',
            label: 'Nested table expand',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="data-table-nested-table-expand"
                  type="checkbox"
                  checked={nestedTableExpand}
                  disabled={!expandable || nestedDoubleExpand}
                  onChange={(event) =>
                    setNestedTableExpand(event.target.checked)
                  }
                />
                <span>Child table with header row</span>
              </label>
            ),
          },
          {
            id: 'data-table-nested-double-expand',
            label: 'Nested expandable rows',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="data-table-nested-double-expand"
                  type="checkbox"
                  checked={nestedDoubleExpand}
                  disabled={!expandable}
                  onChange={(event) => {
                    const enabled = event.target.checked
                    setNestedDoubleExpand(enabled)
                    if (enabled) {
                      setNestedTableExpand(false)
                      setExpandedRowIds((previous) => {
                        const next = previous.slice()
                        if (!next.includes('1')) {
                          next.push('1')
                        }
                        if (!next.includes('1-ledger-1')) {
                          next.push('1-ledger-1')
                        }
                        return next
                      })
                    }
                  }}
                />
                <span>Expanded row within expanded</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      highlightedState,
      highlightedVariant,
      rowCount,
      selectable,
      showPagination,
      showSearch,
      expandable,
      nestedTableExpand,
      nestedDoubleExpand,
      darkMode,
    ],
  )

  const renderVariantPreview = (
    variantId: DataTableShowcaseVariantId,
  ): React.ReactNode => {
    switch (variantId) {
      case 'checkbox-pagination':
        return (
          <DataTable
            columns={DEFAULT_COLUMNS}
            rows={createDefaultRows(3)}
            selectable
            selectedRowIds={['1']}
            previewState="default"
            darkMode={darkMode}
            pagination={STATIC_PAGINATION}
          />
        )
      case 'search':
        return (
          <DataTable
            columns={EXPANDED_COLUMNS}
            rows={createDefaultRows(3)}
            selectable
            selectedRowIds={['1']}
            search={{ placeholder: 'Search' }}
            actionsLabel="Action"
            previewState="default"
            darkMode={darkMode}
            pagination={STATIC_PAGINATION}
          />
        )
      case 'pagination-info':
        return (
          <DataTable
            columns={infoColumns}
            rows={createDefaultRows(3)}
            previewState="default"
            darkMode={darkMode}
            pagination={STATIC_PAGINATION}
          />
        )
      case 'switch-badges':
        return (
          <DataTable
            columns={switchBadgeColumns}
            rows={SWITCH_BADGE_ROWS}
            actionsLabel="Action"
            previewState="default"
            darkMode={darkMode}
          />
        )
      case 'expanded':
        return (
          <DataTable
            columns={EXPANDED_COLUMNS}
            rows={createDefaultRows(3)}
            expandable
            expandedRowIds={['1']}
            actionsLabel="Action"
            previewState="default"
            darkMode={darkMode}
            pagination={STATIC_PAGINATION}
          />
        )
      case 'checkbox-expanded':
        return (
          <DataTable
            columns={EXPANDED_COLUMNS}
            rows={createDefaultRows(3)}
            selectable
            selectedRowIds={['1']}
            expandable
            expandedRowIds={['1']}
            actionsLabel="Action"
            previewState="default"
            darkMode={darkMode}
            pagination={STATIC_PAGINATION}
          />
        )
      case 'expanded-nested-header':
        return (
          <DataTable
            columns={EXPANDED_COLUMNS}
            rows={createDefaultRows(3)}
            expandable
            expandedRowIds={['1']}
            expandedTable={{
              columns: NESTED_HEADER_COLUMNS,
              getRows: getNestedHeaderRows,
            }}
            actionsLabel="Action"
            previewState="default"
            darkMode={darkMode}
            pagination={STATIC_PAGINATION}
          />
        )
      case 'expanded-nested-double':
        return (
          <DataTable
            columns={EXPANDED_COLUMNS}
            rows={createDefaultRows(3)}
            expandable
            expandableColumnId="col1"
            expandedRowIds={['1', '1-ledger-1']}
            expandedTable={{
              columns: NESTED_LEDGER_COLUMNS,
              getRows: getNestedLedgerRows,
              expandable: true,
              expandableColumnId: 'appointment',
              expandedTable: {
                columns: NESTED_PROCEDURE_COLUMNS,
                getRows: getNestedProcedureRows,
              },
            }}
            actionsLabel="Action"
            previewState="default"
            darkMode={darkMode}
            pagination={STATIC_PAGINATION}
          />
        )
      case 'grouped-expanded':
        return (
          <DataTable
            columns={EXPANDED_COLUMNS}
            rows={[]}
            groups={GROUPED_SAMPLE}
            selectable
            expandedRowIds={['group-1']}
            actionsLabel="Action"
            previewState="default"
            darkMode={darkMode}
            pagination={STATIC_PAGINATION}
          />
        )
      default:
        return (
          <DataTable
            columns={DEFAULT_COLUMNS}
            rows={createDefaultRows(3)}
            previewState="default"
            darkMode={darkMode}
          />
        )
    }
  }

  return (
    <>
      <UiPreviewCanvas
        title="Data table"
        description="Tabular data with sortable headers, row selection, actions, pagination, and status states."
        stageClassName="ui-preview__stage--wide data-table-showcase__stage-wrap"
      >
        <div
          className={`data-table-showcase__sheet${
            darkMode ? ' data-table-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Sort columns, select rows, and navigate pages. Enable expandable
              rows for a detail panel, nested table expand for a child header
              table, or nested expandable rows for expanded-within-expanded
              (billing invoice pattern). Only one row, nested table, or nested
              expandable row is open at a time. Expand controls sit on the right
              of the cell.
            </p>
            <div className="data-table-showcase__live">
              <DataTable
                columns={EXPANDED_COLUMNS}
                rows={liveRows}
                selectable={selectable}
                selectedRowIds={selectedRowIds}
                onSelectedRowIdsChange={setSelectedRowIds}
                expandable={expandable}
                expandableColumnId="col1"
                expandedRowIds={expandedRowIds}
                onExpandedRowIdsChange={setExpandedRowIds}
                expandedTable={
                  expandable && nestedDoubleExpand
                    ? {
                        columns: NESTED_LEDGER_COLUMNS,
                        getRows: getNestedLedgerRows,
                        expandable: true,
                        expandableColumnId: 'appointment',
                        expandedTable: {
                          columns: NESTED_PROCEDURE_COLUMNS,
                          getRows: getNestedProcedureRows,
                        },
                      }
                    : expandable && nestedTableExpand
                      ? {
                          columns: NESTED_HEADER_COLUMNS,
                          getRows: getNestedHeaderRows,
                        }
                      : undefined
                }
                actionsLabel="Action"
                darkMode={darkMode}
                renderActions={() => (
                  <ActionButtons
                    darkMode={darkMode}
                    onView={() => {}}
                    onEdit={() => {}}
                    onDelete={() => {}}
                    onDuplicate={() => {}}
                    onPhone={() => {}}
                    onMail={() => {}}
                    onMessage={() => {}}
                  />
                )}
                sortColumnId={sortColumnId}
                sortDirection={sortDirection}
                onSort={handleSort}
                search={
                  showSearch
                    ? {
                        value: searchValue,
                        onChange: setSearchValue,
                      }
                    : undefined
                }
                pagination={
                  showPagination
                    ? {
                        pageSize,
                        currentPage,
                        totalPages: 3,
                        onPageChange: setCurrentPage,
                        onPageSizeChange: setPageSize,
                      }
                    : undefined
                }
              />
            </div>
          </section>

          <div className="data-table-showcase__section-header">
            <span className="data-table-showcase__section-label">Variant</span>
          </div>

          {DATA_TABLE_SHOWCASE_VARIANTS.map((variant) => (
            <div
              key={variant.id}
              ref={(element) => {
                variantRefs.current[variant.id] = element
              }}
              className={`data-table-showcase__variant-row${
                variant.id === highlightedVariant
                  ? ' data-table-showcase__variant-row--selected'
                  : ''
              }`}
            >
              <span className="data-table-showcase__variant-label">
                {variant.label}
              </span>
              <div className="data-table-showcase__variant-preview">
                {renderVariantPreview(variant.id)}
              </div>
            </div>
          ))}

          <div className="data-table-showcase__section-header data-table-showcase__section-header--states">
            <span className="data-table-showcase__section-label">State</span>
          </div>

          {DATA_TABLE_STATES.map((state) => (
            <div
              key={state}
              ref={(element) => {
                stateRefs.current[state] = element
              }}
              className={`data-table-showcase__state-row${
                state === highlightedState
                  ? ' data-table-showcase__state-row--selected'
                  : ''
              }`}
            >
              <span className="data-table-showcase__state-label">
                {DATA_TABLE_STATE_LABELS[state]}
              </span>
              <div className="data-table-showcase__state-preview">
                <DataTable
                  columns={DEFAULT_COLUMNS}
                  rows={createDefaultRows(3)}
                  previewState={state}
                  darkMode={darkMode}
                  selectable={state === 'default'}
                  selectedRowIds={state === 'default' ? ['1'] : []}
                  pagination={
                    state === 'default' ? STATIC_PAGINATION : undefined
                  }
                  emptyState={{
                    actionLabel: 'Clear filters',
                  }}
                  errorState={{
                    actionLabel: 'Refresh',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Data table"
        sections={propertySections}
      />
    </>
  )
}

export default DataTableShowcase
